import { useState } from "react";
import { Check, Minus, Loader2, Sparkles, LogIn, Info } from "lucide-react";
import { toast } from "sonner";
import { paymentsApi } from "../lib/api";
import { useUsdRate, fmtPrice } from "../lib/price";
import { useAuth } from "../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const PLANS = [
  {
    id: "guest",
    title: "Guest",
    eur: null,
    price: "Free",
    accent: "#a89e92",
    searches: "5 searches / day",
    ai: "No AI Expert",
    extras: ["Notation, playback & PDF export", "Favourites on this device only"],
  },
  {
    id: "free",
    title: "Signed in",
    eur: null,
    price: "Free",
    accent: "#2d2824",
    searches: "20 searches / day",
    ai: "3 welcome AI analyses",
    extras: ["Favourites synced across devices", "Everything in Guest"],
  },
  {
    id: "premium_monthly",
    title: "Premium",
    eur: 2,
    unit: "/ month",
    accent: "#8a2be2",
    highlight: true,
    searches: "Unlimited searches",
    ai: "10 AI analyses / month",
    extras: ["Manual data refresh", "Cancel any time in two clicks"],
  },
  {
    id: "premium_annual",
    title: "1 year pass",
    eur: 20,
    unit: "/ year",
    accent: "#0f8a5f",
    searches: "Unlimited searches",
    ai: "15 AI analyses / month",
    extras: ["Manual data refresh", "Best value · 2 months free"],
  },
];

const PACKS = [
  {
    id: "calls_50",
    eur: 5,
    title: "AI pack · 50",
    sub: "50 expert analyses + 50 extra searches",
    accent: "#8a2be2",
  },
  {
    id: "calls_120",
    eur: 10,
    title: "AI pack · 120",
    sub: "120 expert analyses + 120 extra searches",
    accent: "#0f8a5f",
  },
];

export const PricingDialog = ({ open, onOpenChange }) => {
  const { user, login } = useAuth();
  const [busy, setBusy] = useState("");
  const [usd, setUsd] = useState(false);
  const rate = useUsdRate();

  const act = async (plan) => {
    if (!user) {
      login();
      return;
    }
    setBusy(plan.id);
    try {
      const { checkout_url } = await paymentsApi.checkout(plan.id);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setBusy("");
    }
  };

  const currentPlan = user
    ? user.plan === "monthly"
      ? "premium_monthly"
      : user.plan === "annual"
      ? "premium_annual"
      : user.plan === "founder"
      ? "founder"
      : "free"
    : "guest";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[88vh] max-w-4xl overflow-auto bg-[#fdfbf7]"
        data-testid="pricing-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Foinn.org plans</DialogTitle>
          <DialogDescription>
            Search the whole session, hear it, print it. Premium removes the limits.
          </DialogDescription>
          <button
            onClick={() => setUsd((v) => !v)}
            className="absolute right-12 top-5 rounded-full border border-[#e5e0d8] bg-white px-2.5 py-1 text-xs font-extrabold text-[#2d2824] hover:bg-[#f3efea]"
            data-testid="pricing-currency-toggle"
            title="Show prices in dollars / euro"
          >
            {usd ? "$ USD" : "€ EUR"}
          </button>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p) => {
            const isCurrent = currentPlan === p.id;
            return (
              <div
                key={p.id}
                className="relative flex flex-col rounded-3xl border-2 bg-white p-4"
                style={{
                  borderColor: p.highlight ? p.accent : "#e5e0d8",
                  boxShadow: p.highlight ? `0 12px 34px ${p.accent}22` : "none",
                }}
                data-testid={`plan-${p.id}`}
              >
                {p.highlight && (
                  <span
                    className="absolute -top-3 left-4 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white"
                    style={{ background: p.accent }}
                  >
                    Most popular
                  </span>
                )}
                <div className="font-serif text-2xl font-bold text-[#2d2824]">{p.title}</div>
                <div className="mt-0.5 text-lg font-black" style={{ color: p.accent }}>
                  {p.eur ? `${fmtPrice(p.eur, usd, rate)} ${p.unit}` : p.price}
                </div>

                <ul className="mt-3 flex-1 space-y-2 text-sm text-[#2d2824]">
                  <li className="flex items-start gap-2 font-bold">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: p.accent }} />
                    {p.searches}
                  </li>
                  <li className="flex items-start gap-2 font-bold">
                    {p.id === "guest" ? (
                      <Minus className="mt-0.5 h-4 w-4 shrink-0 text-[#a89e92]" />
                    ) : (
                      <Sparkles className="mt-0.5 h-4 w-4 shrink-0" style={{ color: p.accent }} />
                    )}
                    {p.ai}
                  </li>
                  {p.extras.map((e) => (
                    <li key={e} className="flex items-start gap-2 text-[#6e655b]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#c9c1b5]" />
                      {e}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div
                    className="mt-4 rounded-full border-2 py-2.5 text-center text-sm font-extrabold"
                    style={{ borderColor: `${p.accent}44`, color: p.accent }}
                    data-testid={`plan-current-${p.id}`}
                  >
                    Your plan
                  </div>
                ) : p.id === "guest" ? (
                  <div className="mt-4 py-2.5 text-center text-xs font-semibold text-[#a89e92]">
                    No account needed
                  </div>
                ) : p.id === "free" ? (
                  <button
                    onClick={login}
                    className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#2d2824] py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-[1.02]"
                    data-testid="plan-signin"
                  >
                    <LogIn className="h-4 w-4" /> Sign in free
                  </button>
                ) : (
                  <button
                    onClick={() => act(p)}
                    disabled={busy === p.id}
                    className="mt-4 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                    style={{ background: p.accent }}
                    data-testid={`plan-buy-${p.id}`}
                  >
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Choose"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div>
          <h4 className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[#6e655b]">
            Add-on packs · one-off, never expire
          </h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PACKS.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-3 rounded-3xl border-2 border-[#e5e0d8] bg-white p-4"
                data-testid={`pack-${p.id}`}
              >
                <div className="min-w-0">
                  <div className="font-serif text-xl font-bold text-[#2d2824]">{p.title}</div>
                  <div className="flex items-start gap-1.5 text-xs font-bold text-[#6e655b]">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: p.accent }} />
                    {p.sub}
                  </div>
                </div>
                <button
                  onClick={() => act(p)}
                  disabled={busy === p.id}
                  className="shrink-0 rounded-full px-5 py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-105 disabled:opacity-60"
                  style={{ background: p.accent }}
                  data-testid={`pack-buy-${p.id}`}
                >
                  {busy === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    fmtPrice(p.eur, usd, rate)
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        <p className="flex items-start gap-2 text-xs text-[#6e655b]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Cached expert analyses are always free — you only spend an analysis when a tune has never
          been studied before. Subscriptions renew automatically and can be stopped in two clicks
          from Your account (you keep access until the end of the paid period).
          {usd && " Dollar amounts are indicative — Stripe charges in euro."}
        </p>
      </DialogContent>
    </Dialog>
  );
};
